import { Button, Form, Input, Skeleton, Table } from "antd";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { formatPhone } from "@/libs/util";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/formatDate.js";
import EOBManageButton from "@/components/TableActions/EOBManageButton";
import HICFManageButton from "@/components/TableActions/HICFManageButton";
import { ColumnType } from "antd/es/table";
import { useQueryAssistanceRequests } from "@/hooks/useQueryAssistanceRequests";
import useQueryPatientInfo from "@/hooks/useQueryPatientInfo";
import useGlobalConfig from "@/store/global";

const AssistanceRequests = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [requestId, setRequestId] = useState("");
  const [medicalRecordNumber, setMedicalRecordNumber] = useState("");
  const { themeColor } = useGlobalConfig();

  const { data: result, isLoading: assistanceRequestsLoading, refetch: refetchAssistanceRequests } = useQueryAssistanceRequests({
    patientId: patientId as string,
    page: currentPage,
    requestId: requestId,
    medicalRecordNumber: medicalRecordNumber,
  });

  const { data: patientInfo, isLoading: patientInfoLoading } = useQueryPatientInfo(patientId);

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
  }

  const handleSearch = (values: any) => {
    setCurrentPage(1);
    setRequestId(values.requestId || '');
    setMedicalRecordNumber(values.medicalRecordNumber || '');
  }

  const columns: ColumnType<any>[] = [
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      align: "center",
      width: "120px",
    },
    {
      title: "Date Requested",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      width: "160px",
      render: (text: string) => formatDate(text),
    },
    {
      title: "Date of Dispensing",
      dataIndex: "expected_dispensing_date",
      key: "expected_dispensing_date",
      align: "center",
      render: (text: string) => formatDate(text),
    },
    {
      title: "Status",
      key: "status",
      width: "320px",
      dataIndex: "status",
      align: "center",
      render: (_: string, record: any) => (
        <div className="flex flex-col gap-y-2">
          <EOBManageButton record={record} onSuccess={refetchAssistanceRequests} />
          <HICFManageButton record={record} onSuccess={refetchAssistanceRequests} />
        </div>
      ),
    },
    {
      title: "Medical Record",
      dataIndex: "patient_medical_record_number",
      key: "patient_medical_record_number",
      align: "center",
    },
    {
      title: "Fill",
      dataIndex: "fill_num",
      key: "fill_num",
      align: "center",
    },
    {
      title: "Actions",
      key: "action",
      align: "center",
      width: "120px",
      render: (_: string, record: any) => (
        <div className="flex items-center justify-center gap-x-2">
          <Button size="small" onClick={() => navigate(`/assistance_requests/${record.id}/show`)}>View Details</Button>
          <Button size="small" onClick={() => navigate(`/assistance_requests/${record.id}/edit`)}>Edit Request</Button>
          <Button size="small" onClick={() => navigate(`/assistance_requests/${record.id}/refill`)}>Request a Refill</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="py-8">
      <div className="mb-8">
        {
          patientInfoLoading ? (
            <Skeleton active />
          ) : (
            <>
              <h2 className="mb-2 text-xl font-bold">
                Record history of {patientInfo?.patient_first_name}{" "}
                {patientInfo?.patient_last_name}
              </h2>
              <div className="mb-4 space-y-1">
                <p className="space-x-1.5">
                  <strong>Street Address:</strong> <span>{patientInfo?.patient_street_address}</span>
                </p>
                <p className="space-x-1.5">
                  <strong>City:</strong> <span>{patientInfo?.patient_city_address}</span>
                </p>
                <p className="space-x-1.5">
                  <strong>State:</strong> <span>{patientInfo?.patient_state}</span>
                </p>
                <p className="space-x-1.5">
                  <strong>Zip:</strong> <span>{patientInfo?.patient_zip}</span>
                </p>
                <p className="space-x-1.5">
                  <strong>Phone:</strong> <span>{formatPhone(patientInfo?.patient_phone_number)}</span>
                </p>
                <p className="space-x-1.5">
                  <strong>Reg Code:</strong> <span>{patientInfo?.registration_code}</span>
                </p>
              </div>
            </>
          )
        }
      </div>
      <h2 className="mb-4 text-xl font-bold">
        Search Requests
      </h2>
      <Form layout="inline" className="mb-6 flex items-end" onFinish={handleSearch}>
        <Form.Item name="requestId" label="Request ID">
          <Input 
            type="number" 
            placeholder="Request ID" 
            allowClear 
          />
        </Form.Item>
        <Form.Item name="medicalRecordNumber" label="Medical Record">
          <Input 
            placeholder="Medical Record Number" 
            allowClear 
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={assistanceRequestsLoading}
            loading={assistanceRequestsLoading}
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
          >
            Search
          </Button>
        </Form.Item>
      </Form>
      <Table
        loading={assistanceRequestsLoading}
        rowKey="id"
        bordered
        scroll={{
          x: 'max-content',
        }}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          total: result?.pagination.total,
          showSizeChanger: false,
          pageSize: 10,
        }}
        columns={columns}
        dataSource={result?.data}
      />
    </div>
  );
};

export default AssistanceRequests;
